import { Card as DaisyCard, CardProps } from "react-daisyui";
import { publicImageUrl } from "src/core/utilities/publicImageUrl";

export const stringToColour = (str: string) => {
    // Compute a deterministic hash from the string
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Derive HSL values based on the hash
    const hue = Math.abs(hash) % 360; // full colour wheel
    const saturation = 55 + (Math.abs(hash >> 3) % 25); // 55–80%
    const lightness = 70 + (Math.abs(hash >> 4) % 10); // 70–80%

    // Helper: convert HSL to RGB
    const h = hue / 360;
    const s = saturation / 100;
    const l = lightness / 100;

    const hueToRgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    };

    let r: number, g: number, b: number;

    if (s === 0) {
        r = g = b = l; // achromatic
    } else {
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hueToRgb(p, q, h + 1 / 3);
        g = hueToRgb(p, q, h);
        b = hueToRgb(p, q, h - 1 / 3);
    }

    const toHex = (x: number) =>
        Math.round(x * 255)
            .toString(16)
            .padStart(2, "0");

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export const colourClassForHex = (hex: string) => {
    // strip the leading # if it's there
    if (hex.slice(0, 1) === "#") {
        hex = hex.slice(1);
    }

    const rgb = parseInt(hex, 16);

    const redComponent = (rgb >> 16) & 0xff; // extract red
    const greenComponent = (rgb >> 8) & 0xff; // extract green
    const blueComponent = (rgb >> 0) & 0xff; // extract blue

    const luma = 0.2126 * redComponent + 0.7152 * greenComponent + 0.0722 * blueComponent; // per ITU-R BT.709

    return luma < 140 ? "text-white" : "text-neutral";
};

interface Props extends CardProps {
    figureSeed?: string;
    smallFigure?: boolean;
}

interface PublicImageProps {
    url: string | null | undefined;
    alt: string;
}

// Renders an author-supplied image, or nothing at all where the URL is not one we will hotlink
const PublicImage = ({ url, alt }: PublicImageProps) => {
    const src = publicImageUrl(url);

    return src ? <DaisyCard.Image src={src} alt={alt} /> : null;
};

export const Card = ({ className, children, figureSeed, smallFigure, ...props }: Props) => (
    <DaisyCard {...props} className={["border-base-300 shadow-sm bg-base-100", className].filter(Boolean).join(" ")}>
        {figureSeed && (
            <figure
                className={`glass ${smallFigure ? "h-2" : "h-32"}`}
                style={{ backgroundColor: stringToColour(figureSeed) }}
            />
        )}

        {children}
    </DaisyCard>
);

Card.displayName = "Card";

Card.Actions = DaisyCard.Actions;
Card.Body = DaisyCard.Body;
Card.Image = DaisyCard.Image;
Card.PublicImage = PublicImage;
Card.Title = DaisyCard.Title;
